import React, { useRef, useMemo, useEffect, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Line, Html } from '@react-three/drei';
import * as THREE from 'three';

/**
 * DynamicTrajectoryViewer - Time-based 4D trajectory visualization
 *
 * Renders animated skeletal tracking and pitch trajectories with temporal coloring
 * Based on D4RT research: https://deepmind.google/blog/d4rt-teaching-ai-to-see-the-world-in-four-dimensions/
 */

// ============================================================================
// Animated Skeletal Bones
// ============================================================================

function SkeletalBone({ start, end, confidence, color = '#00ffff' }) {
  const points = useMemo(() => [
    new THREE.Vector3(start[0], start[1], start[2]),
    new THREE.Vector3(end[0], end[1], end[2]),
  ], [start, end]);

  const lineColor = useMemo(() => {
    // Confidence-based color intensity
    const baseColor = new THREE.Color(color);
    const dimFactor = 0.3 + confidence * 0.7; // 0.3-1.0 range
    return baseColor.clone().multiplyScalar(dimFactor);
  }, [color, confidence]);

  return (
    <Line
      points={points}
      color={lineColor}
      lineWidth={2}
      transparent
      opacity={0.6 + confidence * 0.4}
    />
  );
}

// ============================================================================
// Animated Joint Sphere
// ============================================================================

function JointSphere({ position, confidence, name, isPrimary = false }) {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);

  // Pulse animation for low confidence
  useFrame(({ clock }) => {
    if (meshRef.current && confidence < 0.7) {
      const scale = 1.0 + Math.sin(clock.elapsedTime * 3) * 0.2;
      meshRef.current.scale.setScalar(scale);
    }
  });

  const size = isPrimary ? 0.08 : 0.05;
  const color = useMemo(() => {
    if (confidence > 0.9) return '#00ff00'; // Green = high confidence
    if (confidence > 0.7) return '#ffff00'; // Yellow = medium
    return '#ff0000'; // Red = low confidence
  }, [confidence]);

  return (
    <mesh
      ref={meshRef}
      position={position}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <sphereGeometry args={[size, 16, 16]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={hovered ? 1.0 : 0.5}
        transparent
        opacity={0.8}
      />
      {hovered && (
        <Html position={[0, size + 0.1, 0]}>
          <div className="bg-black/80 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
            {name} ({(confidence * 100).toFixed(0)}%)
          </div>
        </Html>
      )}
    </mesh>
  );
}

// ============================================================================
// Skeletal Frame Renderer
// ============================================================================

function SkeletalFrame({ frameData, bonePairs }) {
  // Create joint lookup
  const jointMap = useMemo(() => {
    if (!frameData || !frameData.joints) return {};
    const map = {};
    frameData.joints.forEach(joint => {
      map[joint.name] = joint;
    });
    return map;
  }, [frameData]);

  if (!frameData || !frameData.joints) return null;

  return (
    <group>
      {/* Render bones */}
      {bonePairs.map(([startJoint, endJoint], idx) => {
        const start = jointMap[startJoint];
        const end = jointMap[endJoint];

        if (!start || !end) return null;

        const startPos = [
          start.position_3d.x,
          start.position_3d.y,
          start.position_3d.z,
        ];

        const endPos = [
          end.position_3d.x,
          end.position_3d.y,
          end.position_3d.z,
        ];

        const avgConfidence = (start.confidence + end.confidence) / 2;

        return (
          <SkeletalBone
            key={`bone-${idx}`}
            start={startPos}
            end={endPos}
            confidence={avgConfidence}
            color="#00ffff"
          />
        );
      })}

      {/* Render joints */}
      {frameData.joints.map((joint, idx) => {
        const pos = [
          joint.position_3d.x,
          joint.position_3d.y,
          joint.position_3d.z,
        ];

        const isPrimary = ['right_hand', 'right_elbow', 'right_shoulder'].includes(joint.name);

        return (
          <JointSphere
            key={`joint-${idx}`}
            position={pos}
            confidence={joint.confidence}
            name={joint.name}
            isPrimary={isPrimary}
          />
        );
      })}
    </group>
  );
}

// ============================================================================
// Pitch Trajectory Line
// ============================================================================

function PitchTrajectory({ trajectoryData, currentTime }) {
  const points = useMemo(() => {
    if (!trajectoryData || !trajectoryData.trajectory) return [];

    return trajectoryData.trajectory.map(point =>
      new THREE.Vector3(
        point.position_3d.x,
        point.position_3d.y,
        point.position_3d.z
      )
    );
  }, [trajectoryData]);

  // Time-based coloring: blue (past) → cyan (present) → green (future)
  const colors = useMemo(() => {
    if (!trajectoryData || !trajectoryData.trajectory) return [];

    const totalFrames = trajectoryData.trajectory.length;

    return trajectoryData.trajectory.map((point, idx) => {
      const frameTime = idx / Math.max(totalFrames - 1, 1);

      // Color based on time relative to current playback
      let color;
      if (frameTime < currentTime - 0.1) {
        color = new THREE.Color(0x0000ff); // Blue = past
      } else if (frameTime < currentTime + 0.1) {
        color = new THREE.Color(0x00ffff); // Cyan = present
      } else {
        color = new THREE.Color(0x00ff00); // Green = future
      }

      return color;
    });
  }, [trajectoryData, currentTime]);

  if (points.length === 0) return null;

  return (
    <Line
      points={points}
      vertexColors={colors}
      lineWidth={3}
      transparent
      opacity={0.8}
    />
  );
}

// ============================================================================
// Main DynamicTrajectoryViewer Component
// ============================================================================

export default function DynamicTrajectoryViewer({
  skeletalData = null,
  pitchData = null,
  isPlaying = false,
  playbackSpeed = 1.0,
  onFrameChange = null,
  className = '',
}) {
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const frameTimerRef = useRef(null);

  // Major bone pairs for skeletal rendering
  const bonePairs = useMemo(() => [
    // Spine
    ['head', 'neck'],
    ['neck', 'spine'],
    ['spine', 'hips'],

    // Right arm (throwing)
    ['right_shoulder', 'right_elbow'],
    ['right_elbow', 'right_wrist'],
    ['right_wrist', 'right_hand'],

    // Left arm
    ['left_shoulder', 'left_elbow'],
    ['left_elbow', 'left_wrist'],
    ['left_wrist', 'left_hand'],

    // Torso
    ['neck', 'right_shoulder'],
    ['neck', 'left_shoulder'],
    ['spine', 'right_shoulder'],
    ['spine', 'left_shoulder'],

    // Legs
    ['hips', 'right_knee'],
    ['right_knee', 'right_ankle'],
    ['hips', 'left_knee'],
    ['left_knee', 'left_ankle'],
  ], []);

  // Animation loop
  useEffect(() => {
    if (!isPlaying || !skeletalData) return;

    const fps = 30;
    const frameInterval = 1000 / (fps * playbackSpeed);

    frameTimerRef.current = setInterval(() => {
      setCurrentFrameIndex(prev => {
        const maxFrames = skeletalData.frames?.length || 0;
        if (maxFrames === 0) return 0;

        const next = (prev + 1) % maxFrames;
        const normalizedTime = next / Math.max(maxFrames - 1, 1);
        setCurrentTime(normalizedTime);

        if (onFrameChange) {
          onFrameChange(next, skeletalData.frames[next]);
        }

        return next;
      });
    }, frameInterval);

    return () => {
      if (frameTimerRef.current) {
        clearInterval(frameTimerRef.current);
      }
    };
  }, [isPlaying, skeletalData, playbackSpeed, onFrameChange]);

  const currentFrame = useMemo(() => {
    if (!skeletalData || !skeletalData.frames) return null;
    return skeletalData.frames[currentFrameIndex] || null;
  }, [skeletalData, currentFrameIndex]);

  return (
    <div className={`relative w-full h-full bg-black ${className}`}>
      <Canvas>
        <PerspectiveCamera makeDefault position={[0, 2, 25]} fov={60} />
        <OrbitControls
          enableDamping
          dampingFactor={0.05}
          minDistance={5}
          maxDistance={50}
          target={[0, 1.5, 18]}
        />

        {/* Lighting */}
        <ambientLight intensity={0.4} />
        <directionalLight position={[10, 10, 5]} intensity={1.0} />
        <pointLight position={[-10, -10, -5]} intensity={0.5} />

        {/* Ground plane (pitcher's mound) */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 18]}>
          <planeGeometry args={[30, 30]} />
          <meshStandardMaterial color="#1a5a1a" transparent opacity={0.3} />
        </mesh>

        {/* Grid */}
        <gridHelper args={[30, 30, '#00ffff', '#004444']} position={[0, 0, 18]} />

        {/* Render skeletal frame */}
        {currentFrame && (
          <SkeletalFrame
            frameData={currentFrame}
            bonePairs={bonePairs}
          />
        )}

        {/* Render pitch trajectory */}
        {pitchData && (
          <PitchTrajectory
            trajectoryData={pitchData}
            currentTime={currentTime}
          />
        )}
      </Canvas>

      {/* Playback controls overlay */}
      <div className="absolute bottom-4 left-4 right-4 bg-black/80 rounded-lg p-3 border border-cyan-500/30">
        <div className="flex items-center gap-4">
          <div className="text-cyan-400 text-sm font-mono">
            Frame: {currentFrameIndex + 1} / {skeletalData?.frames?.length || 0}
          </div>
          <div className="flex-1 bg-zinc-800 h-2 rounded-full overflow-hidden">
            <div
              className="h-full bg-cyan-500 transition-all"
              style={{ width: `${currentTime * 100}%` }}
            />
          </div>
          <div className="text-zinc-400 text-xs">
            {(currentTime * 100).toFixed(1)}%
          </div>
        </div>

        {/* Pitch data overlay */}
        {pitchData && (
          <div className="mt-2 flex gap-4 text-xs">
            <div className="text-green-400">
              Release: {pitchData.release_velocity_mph?.toFixed(1)} mph
            </div>
            <div className="text-yellow-400">
              Plate: {pitchData.plate_velocity_mph?.toFixed(1)} mph
            </div>
            <div className="text-cyan-400">
              Spin: {pitchData.spin_rate_rpm?.toFixed(0)} rpm
            </div>
            <div className="text-purple-400">
              Break: {pitchData.break_inches?.vertical?.toFixed(1)}" V, {pitchData.break_inches?.horizontal?.toFixed(1)}" H
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
